import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, Navigate, NavLink, useNavigate, useParams } from 'react-router-dom';
import { api } from '../api';
import { can } from '../auth/me';
import { useMe } from '../auth/useMe';
import type { Me } from '../auth/me';
import { formatPln } from '../catalog/money';
import { AllocateDialog } from './AllocateDialog';
import { ManualPurchaseForm } from './ManualPurchaseForm';
import { dayLabel, errorText, INVESTMENTS_RESOURCE, PURCHASES_KEY, PURCHASES_RESOURCE, STATE_LABELS, TARGET_LABELS, type Purchase, type PurchaseDetail, type PurchaseState } from './types';
import '../finance/finance.css';
import './purchases.css';

const money = (v: number | undefined) => (v === undefined ? '—' : formatPln(v));

/** "Faktury zakupowe" / "Inwestycje": the two parts of "Zakupy", each for whoever may view it. */
export function PurchasesSegments({ me }: { me: Me }) {
  const purchases = can(me, PURCHASES_RESOURCE, 'view');
  const investments = can(me, INVESTMENTS_RESOURCE, 'view');
  if (!purchases || !investments) return null;
  return (
    <nav className="segment purchases-segments" aria-label="Zakupy">
      <NavLink to="/finanse/zakupy" end>
        Faktury zakupowe
      </NavLink>
      <NavLink to="/finanse/zakupy/inwestycje">Inwestycje</NavLink>
    </nav>
  );
}

const FILTERS: Array<{ key: PurchaseState | ''; label: string }> = [
  { key: 'do_rozpisania', label: 'Do rozpisania' },
  { key: '', label: 'Wszystkie' },
];

/** "Finanse → Zakupy → Faktury zakupowe" (F42): purchase invoices with their state; "Dodaj fakturę" for one outside KSeF. */
export function PurchasesPage() {
  const { me } = useMe();
  const navigate = useNavigate();
  const [state, setState] = useState<PurchaseState | ''>('');
  const [adding, setAdding] = useState(false);
  const list = useQuery({ queryKey: [...PURCHASES_KEY, 'list', state], queryFn: () => api<{ purchases: Purchase[]; truncated: boolean }>(`/api/purchases${state ? `?state=${state}` : ''}`) });
  if (!me) return null;
  if (!can(me, PURCHASES_RESOURCE, 'view')) return <Navigate to="/finanse/zakupy/inwestycje" replace />;
  const rows = list.data?.purchases ?? [];
  const showAmounts = rows.some((r) => r.netGrosze !== undefined);
  return (
    <section className="page wide purchases-page" data-feature-id="purchases.list">
      <PurchasesSegments me={me} />
      <div className="purchases-head">
        <p className="lede">Faktury zakupowe do rozpisania na katalog, inwestycje albo koszty.</p>
        {can(me, PURCHASES_RESOURCE, 'create') && !adding && (
          <button className="btn primary" type="button" onClick={() => setAdding(true)}>
            Dodaj fakturę
          </button>
        )}
      </div>
      {adding && <ManualPurchaseForm onCancel={() => setAdding(false)} onDone={(p) => navigate(`/finanse/zakupy/${p.id}`)} />}
      <div className="segment" role="group" aria-label="Stan">
        {FILTERS.map((f) => (
          <button key={f.label} type="button" aria-pressed={state === f.key} onClick={() => setState(f.key)}>
            {f.label}
          </button>
        ))}
      </div>
      {list.error && (
        <p className="alert" role="alert">
          {errorText(list.error, 'Nie udało się wczytać faktur zakupowych')}
        </p>
      )}
      {list.data && rows.length === 0 && <p className="muted">Brak faktur zakupowych.</p>}
      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="table fin-table" aria-label="Faktury zakupowe">
            <thead>
              <tr>
                <th scope="col">Data</th>
                <th scope="col">Sprzedawca</th>
                <th scope="col">Numer</th>
                {showAmounts && (
                  <>
                    <th scope="col" className="num">
                      Netto
                    </th>
                    <th scope="col" className="num wide-only">
                      Brutto
                    </th>
                  </>
                )}
                <th scope="col">Stan</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} className="clickable" onClick={() => navigate(`/finanse/zakupy/${p.id}`)}>
                  <td className="nowrap">{dayLabel(p.issueDate)}</td>
                  <td>{p.sellerName}</td>
                  <td>
                    <Link to={`/finanse/zakupy/${p.id}`} onClick={(e) => e.stopPropagation()}>
                      {p.invoiceNumber}
                    </Link>
                  </td>
                  {showAmounts && (
                    <>
                      <td className="num nowrap">{money(p.netGrosze)}</td>
                      <td className="num nowrap wide-only">{money(p.grossGrosze)}</td>
                    </>
                  )}
                  <td>
                    <span className={`purchase-state is-${p.state}`}>{STATE_LABELS[p.state]}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {list.data?.truncated && <p className="muted">Pokazano 500 najnowszych faktur.</p>}
    </section>
  );
}

/** One purchase invoice: its data, file, parts, and "Rozpisz" / "Pomiń" while it waits. */
export function PurchaseDetailPage() {
  const { id = '' } = useParams();
  const { me } = useMe();
  const queryClient = useQueryClient();
  const [allocating, setAllocating] = useState(false);
  const detail = useQuery({ queryKey: [...PURCHASES_KEY, 'detail', id], queryFn: () => api<PurchaseDetail>(`/api/purchases/${id}`) });
  const skip = useMutation({
    mutationFn: (p: PurchaseDetail) => api(`/api/purchases/${p.id}/skip`, { method: 'POST', body: { version: p.version } }),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: PURCHASES_KEY }),
  });
  if (!me) return null;
  const p = detail.data;
  return (
    <section className="page wide purchases-page" data-feature-id="purchases.list">
      <p>
        <Link to="/finanse/zakupy">← Faktury zakupowe</Link>
      </p>
      {detail.error && (
        <p className="alert" role="alert">
          {errorText(detail.error, 'Nie udało się wczytać faktury')}
        </p>
      )}
      {p && (
        <>
          <h2 className="purchase-title">
            {p.invoiceNumber} · {p.sellerName}
          </h2>
          <dl className="data-list purchase-data">
            <div className="data-row">
              <dt className="muted">Stan</dt>
              <dd>
                <span className={`purchase-state is-${p.state}`}>{STATE_LABELS[p.state]}</span>
                {p.skipReason && ` — ${p.skipReason}`}
              </dd>
            </div>
            <div className="data-row">
              <dt className="muted">Data wystawienia</dt>
              <dd>{dayLabel(p.issueDate)}</dd>
            </div>
            {p.dueDate && (
              <div className="data-row">
                <dt className="muted">Termin płatności</dt>
                <dd>{dayLabel(p.dueDate)}</dd>
              </div>
            )}
            {p.sellerNip && (
              <div className="data-row">
                <dt className="muted">NIP sprzedawcy</dt>
                <dd>{p.sellerNip}</dd>
              </div>
            )}
            {p.netGrosze !== undefined && (
              <div className="data-row">
                <dt className="muted">Netto / VAT / brutto</dt>
                <dd>
                  {money(p.netGrosze)} / {money(p.vatGrosze)} / {money(p.grossGrosze)}
                </dd>
              </div>
            )}
            {p.fileId && (
              <div className="data-row">
                <dt className="muted">Plik</dt>
                <dd>
                  <a href={`/api/files/${p.fileId}`} target="_blank" rel="noreferrer">
                    Otwórz fakturę
                  </a>
                </dd>
              </div>
            )}
          </dl>
          {p.allocations.length > 0 && (
            <div className="table-wrap">
              <table className="table fin-table" aria-label="Części faktury">
                <thead>
                  <tr>
                    <th scope="col">Rodzaj</th>
                    <th scope="col">Co</th>
                    <th scope="col" className="num">
                      Ilość
                    </th>
                    {p.allocations.some((a) => a.netGrosze !== undefined) && (
                      <th scope="col" className="num">
                        Netto
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {p.allocations.map((a) => (
                    <tr key={a.id}>
                      <td>{TARGET_LABELS[a.target]}</td>
                      <td>{a.catalogItemId ? a.catalogItemName ? <Link to={`/katalog?szukaj=${encodeURIComponent(a.catalogItemName)}`}>{a.catalogItemName}</Link> : 'pozycja usunięta' : (a.investmentName ?? a.description) || '—'}</td>
                      <td className="num">{a.quantity ?? ''}</td>
                      {a.netGrosze !== undefined && <td className="num nowrap">{formatPln(a.netGrosze)}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {p.state !== 'rozpisana' && can(me, PURCHASES_RESOURCE, 'edit') && p.netGrosze !== undefined && !allocating && (
            <div className="actions row-actions">
              <button className="btn primary" type="button" onClick={() => setAllocating(true)}>
                Rozpisz
              </button>
              {p.state === 'do_rozpisania' && (
                <button className="btn ghost" type="button" onClick={() => skip.mutate(p)} disabled={skip.isPending}>
                  Pomiń
                </button>
              )}
            </div>
          )}
          {skip.error && (
            <p className="alert" role="alert">
              {errorText(skip.error, 'Nie udało się pominąć faktury')}
            </p>
          )}
          {allocating && <AllocateDialog purchase={p} onClose={() => setAllocating(false)} />}
        </>
      )}
    </section>
  );
}
